/* eslint-disable tss-unused-classes/unused-classes */
import { makeStyles } from 'tss-react/mui';
import { CustomFilter } from 'app/ComponentLibrary/Filter';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { Collapse, Grid } from '@mui/material';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { useDispatch, useSelector } from 'react-redux';
import { selectGridFiltersExpanded } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { useFormContext } from 'react-hook-form';
import { getPlacementStatusTreeOptions } from 'app/services/SharedServices/SharedServices';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { usePlacementPayPackagesFilterValues } from '@AMIEWEB/Order/OrderDetails/PayPackages/PlacementPayPackages/usePlacementPayPackagesFilterValues';
import {
  defaultFilterValues,
  defaultPayPackageStatusOptions,
  PlacementPayPackagesFilterName,
  PlacementPayPackagesFilterSpecs,
  reorderByField,
} from '@AMIEWEB/Order/OrderDetails/PayPackages/PlacementPayPackages/filterUtils';
import { IGetPlacementPayPackagesQuery } from '@AMIEWEB/Order/Store/types';

export const CandidatePlacementPayPackagesFilters = ({
  candidateId,
  saveFilterPreferences,
  onRefresh,
}: {
  candidateId: number;
  saveFilterPreferences?: (filterValues, newSelectedChipFilters?) => void;
  onRefresh?: () => void;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset } = useFormContext();
  const gridSpecs = useRef(
    new PlacementPayPackagesFilterSpecs(
      {
        statusOpts: [],
        payPackageStatusOpts: defaultPayPackageStatusOptions,
        orderIdOpts: [],
      },
      t,
      { includeCandidateFilters: false, includeOrderIdFilter: true },
    ),
  );
  const baseColumns = gridSpecs.current.getSpecs();

  const orderedColumns = reorderByField(baseColumns, [
    PlacementPayPackagesFilterName.orderId,
    PlacementPayPackagesFilterName.placementStatus,
    PlacementPayPackagesFilterName.payPackageStatus,
    PlacementPayPackagesFilterName.archiveStatus,
  ]);
  const [fields, setFields] = useState<any>(orderedColumns);

  const filterValuesFromApi = usePlacementPayPackagesFilterValues(
    { candidateId },
    { includeOrderId: true, includeCandidates: false },
  );

  const fetchSpecs = async () => {
    getPlacementStatusTreeOptions().then(res => {
      gridSpecs.current.filterSpecs.statusOpts = res || [];
      const baseColumns = gridSpecs.current.getSpecs();

      const orderedColumns = reorderByField(baseColumns, [
        PlacementPayPackagesFilterName.orderId,
        PlacementPayPackagesFilterName.placementStatus,
        PlacementPayPackagesFilterName.payPackageStatus,
        PlacementPayPackagesFilterName.archiveStatus,
      ]);
      setFields(orderedColumns);
    });
  };

  useEffect(() => {
    gridSpecs.current.filterSpecs.orderIdOpts = filterValuesFromApi.orderIdOpts;
    const baseColumns = gridSpecs.current.getSpecs();

    const orderedColumns = reorderByField(baseColumns, [
      PlacementPayPackagesFilterName.orderId,
      PlacementPayPackagesFilterName.placementStatus,
      PlacementPayPackagesFilterName.payPackageStatus,
      PlacementPayPackagesFilterName.archiveStatus,
    ]);
    setFields(orderedColumns);
  }, [filterValuesFromApi.orderIdOpts]);

  const resetFilter = () => {
    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(defaultFilterValues),
        selectedChipFilters: null,
      }),
    );
    reset({ ...defaultFilterValues });
    if (saveFilterPreferences) {
      saveFilterPreferences(defaultFilterValues, null);
    }
  };

  const refreshData = () => {
    if (onRefresh) {
      onRefresh();
    } else if (Number(candidateId) > 0) {
      dispatch(orderDataActions.getPlacementPayPackages({ candidateId: Number(candidateId) } as IGetPlacementPayPackagesQuery));
    }
  };

  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  useEffect(() => {
    trackPromise(fetchSpecs(), 'placement-pay-packages-filter-specs');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter fields={fields} justifyContent={'flex-start'} version2 resetBtn onResetForm={resetFilter} />
        </Collapse>
      </Grid>
      <Grid item xs={1} className={classes.fillterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          onResetForm={() => resetFilter()}
          onAdvancedFilterClick={() => {
            handleChange();
          }}
          checked={filtersExpanded}
        />
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
});
